import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink, Image as ImageIcon, Link2, Play } from 'lucide-react';

interface ExampleWorkUrlCardProps {
    type?: 'video' | 'image';
    thumbnailUrl?: string;
    videoUrl?: string;
    images?: string[];
    title: string;
    description?: string;
    sourceUrl: string;
    onClick?: () => void;
}

const ExampleWorkUrlCard: React.FC<ExampleWorkUrlCardProps> = ({
    type = 'image',
    thumbnailUrl,
    videoUrl,
    images,
    title,
    description,
    sourceUrl,
    onClick
}) => {
    const [hasPreviewError, setHasPreviewError] = useState(false);

    const previewImageUrl = useMemo(() => (
        thumbnailUrl || images?.[0] || ''
    ), [images, thumbnailUrl]);

    useEffect(() => {
        setHasPreviewError(false);
    }, [previewImageUrl, sourceUrl, videoUrl]);

    const showImagePreview = !!previewImageUrl && !hasPreviewError;
    const showVideoPreview = type === 'video' && !!videoUrl && !showImagePreview;

    return (
        <div
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick}
            onKeyDown={(event) => {
                if (!onClick) return;
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onClick();
                }
            }}
            className={`group relative aspect-[4/3] overflow-hidden rounded-2xl border border-gray-200 bg-gray-100 text-left transition-all hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-lg ${onClick ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20' : ''}`}
        >
            <div className="absolute inset-0">
                {showImagePreview ? (
                    <img
                        src={previewImageUrl}
                        alt={title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={() => setHasPreviewError(true)}
                    />
                ) : showVideoPreview ? (
                    <video
                        src={videoUrl}
                        muted
                        playsInline
                        preload="metadata"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                ) : (
                    <div className="h-full w-full bg-gradient-to-br from-blue-50 via-white to-slate-100" />
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/5" />
            </div>

            <div className="relative flex h-full flex-col justify-between p-4">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/90 text-blue-600 shadow-sm backdrop-blur-sm">
                        {type === 'video' ? (
                            <Play className="ml-0.5 h-3.5 w-3.5 fill-current" />
                        ) : showImagePreview ? (
                            <ImageIcon className="h-3.5 w-3.5" />
                        ) : (
                            <Link2 className="h-3.5 w-3.5" />
                        )}
                    </div>
                    <span className="rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold tracking-wide text-gray-600 shadow-sm backdrop-blur-sm">
                        URL
                    </span>
                </div>

                <div className="space-y-1.5">
                    <h4 className="text-lg font-semibold leading-snug text-white line-clamp-1 drop-shadow-lg">
                        {title}
                    </h4>
                    <p className="text-sm leading-5 text-white/80 line-clamp-1 drop-shadow">
                        {description?.trim() || '暂无描述'}
                    </p>
                    <a
                        href={sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        className="inline-flex max-w-full items-center gap-1 text-sm leading-5 text-blue-200 hover:text-blue-100 hover:underline"
                    >
                        <span className="truncate">{sourceUrl}</span>
                        <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                    </a>
                </div>
            </div>
        </div>
    );
};

export default ExampleWorkUrlCard;
